import { type FC, type ReactElement, type ReactNode } from 'react';

import { Stack } from '@craft/ui/components/structural';
import { Typography } from '@craft/ui/components/typography';

import {
  IllustrationDisplay,
  type IllustrationVariant,
} from '@/src/components/atoms/IllustrationDisplay';

const IMAGE_HEIGHT = 195.24;

interface EmptyStateMessageProps {
  /**
   * Component to render in the action area of the message. Typically a link button to a support
   * article.
   */
  readonly action?: ReactNode;
  /**
   * One of the preset variants available for the Illustration component.
   */
  readonly illustrationVariant?: IllustrationVariant;
  /**
   * Boolean to determine if the Illustration component should be rendered.
   */
  readonly isImage?: boolean;
  /**
   * The text to display below the title.
   */
  readonly subTitle?: ReactElement | string;
  /**
   * The text to display as the title.
   */
  readonly title: ReactElement | string;
}

/**
 * Used to display the combination of illustration and messaging to users when there is no data
 * to display. Typically used as the empty state for lists/tables.
 *
 * @see https://www.figma.com/file/kZ6spnfsAZYyn90Ovub3LV/Craft-Component-Library?type=design&node-id=495%3A20869&mode=design&t=CLckj8JhnnJibY6z-1
 *
 * @deprecated
 * Please use EmptyMessage in @craft/ui/components/empty instead.
 */
export const EmptyStateMessage: FC<EmptyStateMessageProps> = ({
  action,
  illustrationVariant = 'directions',
  isImage: image,
  subTitle,
  title,
}) => (
  <Stack alignItems='center' padding={2}>
    <Stack
      alignItems='center'
      justifyContent='center'
      maxWidth='20.5rem'
      minHeight='375px'
      spacing={2}
    >
      {image && (
        <IllustrationDisplay
          altText="You're all caught up image"
          height={IMAGE_HEIGHT}
          variant={illustrationVariant}
          width={IMAGE_HEIGHT}
        />
      )}
      <Typography color='text.secondary' textAlign='center' variant='h6'>
        {title}
      </Typography>
      {subTitle && (
        <Typography color='text.secondary' textAlign='center' variant='body2'>
          {subTitle}
        </Typography>
      )}
      {action}
    </Stack>
  </Stack>
);
